import { DeviceStatus } from "@/Pages/Dashboard/shared/DeviceStatus/types";

import { ApiResources } from "../../api/resources";
import { FILTER_KEYS, FILTER_NAMES } from "../../components/Filters/types";
import { ResourceParams } from "../../components/Schedules/types";
import {
  Column,
  COLUMN_NAMES,
  COLUMN_RESOURCES,
  DetailsDataValue,
  DRIVERS_ACTIONS,
  ROUTES,
  TABLE_NAMES,
  TRANSLATABLE_OBJECTS,
  VEHICLES_ACTIONS
} from "..";

export const getDetailsDataValues = (
  tableName: TABLE_NAMES,
  filterId: string | undefined,
  filterName: string | undefined,
  deviceStatus?: boolean
) => {
  if (deviceStatus && filterName === DeviceStatus.Deactivated) {
    const deactivatedFilterValue = {
      id: filterId,
      name: FILTER_NAMES.DeactivatedVehicles
    };

    return {
      filterValue: deactivatedFilterValue,
      pageLocation: ROUTES.Vehicles,
      filterName: FILTER_NAMES.DeactivatedVehicles
    };
  }

  const filterValueObject = {
    id: filterId,
    name: filterName
  };

  const detailsDataValues: { [key: string]: DetailsDataValue } = {
    [TABLE_NAMES.Vehicles]: {
      filterValue: filterValueObject,
      pageLocation: deviceStatus ? ROUTES.Vehicles : ROUTES.VehicleFiles,
      filterName: deviceStatus
        ? FILTER_NAMES.Statuses
        : FILTER_NAMES.VehicleNumber
    },
    [TABLE_NAMES.Drivers]: {
      filterValue: filterValueObject,
      pageLocation: ROUTES.DriverFiles,
      filterName: FILTER_NAMES.DriverName
    }
  };

  return detailsDataValues[tableName];
};

export const addIsActiveToColumns = (columns: Column[]) =>
  columns.map(column => ({
    ...column,
    isActive: Boolean(column.isDefaultActive)
  }));

export const tableNameParams: { [key: string]: ResourceParams } = {
  [TABLE_NAMES.Vehicles]: {
    resourceName: TABLE_NAMES.Vehicles,
    actionCreate: VEHICLES_ACTIONS.CreateSchedule,
    actionDelete: VEHICLES_ACTIONS.DeleteSchedule,
    actionEdit: VEHICLES_ACTIONS.EditSchedule,
    actionMain: VEHICLES_ACTIONS.Schedules,
    actionRemove: VEHICLES_ACTIONS.RemoveSchedules,
    apiResource: ApiResources.VehicleSchedules,
    apiResourceApply: ApiResources.VehicleSchedulesApply,
    apiResourceApplyToAll: ApiResources.VehicleSchedulesApplyToAll,
    column: COLUMN_NAMES.Vehicle,
    filterKey: FILTER_KEYS.VehicleIds,
    pathname: ROUTES.Vehicles
  },
  [TABLE_NAMES.Drivers]: {
    resourceName: TABLE_NAMES.Drivers,
    actionCreate: DRIVERS_ACTIONS.CreateSchedule,
    actionDelete: DRIVERS_ACTIONS.DeleteSchedule,
    actionEdit: DRIVERS_ACTIONS.EditSchedule,
    actionMain: DRIVERS_ACTIONS.Schedules,
    actionRemove: DRIVERS_ACTIONS.RemoveSchedules,
    apiResource: ApiResources.DriverSchedules,
    apiResourceApply: ApiResources.DriverSchedulesApply,
    apiResourceApplyToAll: ApiResources.DriverSchedulesApplyToAll,
    column: COLUMN_NAMES.Driver,
    filterKey: FILTER_KEYS.DriverIds,
    pathname: ROUTES.Drivers
  }
};

export const convertStringBasedOnLength = (
  resourceLength: number,
  inputString: string
) => {
  const isMultipleSelected = resourceLength > 1;

  const isZero = resourceLength === 0;

  return isMultipleSelected || isZero
    ? `${inputString} multiple`
    : `${inputString} single`;
};

export const checkIfFilesPage = (currPage: string) =>
  currPage === ROUTES.VehicleFiles || currPage === ROUTES.DriverFiles;

export const getDefaultSortedColumn = (
  currPage: string,
  dataFromLocalStorage: any
) => {
  if (checkIfFilesPage(currPage)) {
    return (
      dataFromLocalStorage || {
        orderBy: COLUMN_RESOURCES.DownloadTime,
        descending: true
      }
    );
  }

  return dataFromLocalStorage;
};

export const checkIfTranslationExists = (
  key: string,
  translationObject: TRANSLATABLE_OBJECTS,
  i18n: any
) => {
  const fullKey = `${translationObject}##${key}`;
  const options = { nsSeparator: false };
  return i18n.exists(fullKey, options) ? i18n.t(fullKey, options) : key;
};

// compare versions:
export const compareVersions = (current: string, latest: string): number => {
  // convert the string version to an array of numbers
  const toNumbers = (v: string): number[] => v.split(".").map(Number);

  // make both arrays the same length (filling with 0 if needed)
  const pad = (arr: number[], length: number): number[] => [
    ...arr,
    ...new Array(length - arr.length).fill(0)
  ];

  const a1 = toNumbers(current);
  const a2 = toNumbers(latest);
  const maxLength = Math.max(a1.length, a2.length);

  const v1Parts = pad(a1, maxLength);
  const v2Parts = pad(a2, maxLength);

  // compare each part and find the first non-zero difference
  // if all parts are equal, return 0
  return (
    v1Parts
      .map((num1, i) => Math.sign(num1 - v2Parts[i]))
      .find(diff => diff !== 0) ?? 0
  );
};

export const buildQueryString = (params: Record<string, any>): string => {
  const filtered = Object.entries(params)
    .filter(([_, value]) => value != null && value !== "")
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  return new URLSearchParams(filtered).toString();
};
